// Détection locale (sans IA) pour la création express de patient (Prompt 12 §3).
// Découpe un texte libre en Prénom / Nom / Téléphone / Email, et fournit la
// recherche de doublons côté client. Toutes les valeurs ajustables (libellés,
// délais, civilités) sont regroupées ici.
import { normalizePhone } from "@/lib/phone";

// Délais (ms)
export const PARSE_DEBOUNCE = 150;
export const DUP_DEBOUNCE = 250;
export const UNDO_MS = 8000;

// Libellés exacts (maquettes 7a / 7c)
export const LABELS = {
  title: "Nouveau patient",
  placeholder: "Nom, téléphone ou email",
  help: "Tapez ou collez le nom, le téléphone, l'email, dans n'importe quel ordre.",
  dupTitle: "Existe peut-être déjà",
  open: "Ouvrir",
  addMore: "Ajouter maintenant (facultatif)",
  consent: "Consentement SMS et email recueilli oralement",
  createOpen: "Créer et ouvrir la fiche",
  createRdv: "Créer + RDV",
  createAnother: "Créer + un autre",
  noPatient: "Aucun patient trouvé",
  consentSource: "oral, à la création",
};

// Civilités ignorées (normalisées, sans point final)
const CIVILITES = new Set(["m", "mr", "mme", "mlle", "dr", "docteur", "monsieur", "madame", "mademoiselle", "mx"]);

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
const EMAIL_RE = /[^\s,;:<>()[\]]+@[^\s,;:<>()[\]]+\.[a-zA-Z]{2,}/;
// Numéro FR : commence par +33 / 0033 / 0, puis au moins 8 caractères de numéro
const PHONE_RE = /(?:\+33|0033|0)[\d\s.\-()]{8,}/;

// Met une majuscule à chaque partie d'un prénom composé : "jean-marc" → "Jean-Marc".
function capName(w) {
  return (w || "")
    .split(/([ -])/)
    .map((part) => (/[ -]/.test(part) ? part : part ? part[0].toUpperCase() + part.slice(1).toLowerCase() : part))
    .join("");
}

// Analyse un numéro brut. Renvoie { value, display, kind, incomplete } ou null.
export function parsePhone(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  let d = s.replace(/[^\d+]/g, "");
  if (d.startsWith("0033")) d = "+33" + d.slice(4);
  // Étranger (hors +33) : on garde tel quel, groupé par 2.
  if (d.startsWith("+") && !d.startsWith("+33")) {
    const only = d.replace(/\D/g, "");
    return { value: "+" + only, display: ("+" + only).replace(/(\d{2})(?=\d)/g, "$1 ").trim(), kind: "fixe", incomplete: only.length < 8 };
  }
  let nat = d.startsWith("+33") ? "0" + d.slice(3) : d.replace(/\D/g, "");
  if (nat.length === 9 && !nat.startsWith("0")) nat = "0" + nat;
  const incomplete = nat.length !== 10;
  const two = nat.slice(0, 2);
  const kind = two === "06" || two === "07" ? "mobile" : "fixe";
  const display = nat.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
  return { value: nat, display, kind, incomplete };
}

// Analyse le champ unique → { prenom, nom, email, phone|null }.
export function parseInput(text) {
  const result = { prenom: "", nom: "", email: "", phone: null };
  if (!text || !text.trim()) return result;
  let working = text.replace(/[\n\r\t;]+/g, " ");

  const em = working.match(EMAIL_RE);
  if (em) { result.email = em[0].toLowerCase(); working = working.replace(em[0], " "); }

  const ph = working.match(PHONE_RE);
  if (ph) { const parsed = parsePhone(ph[0]); if (parsed) { result.phone = parsed; working = working.replace(ph[0], " "); } }

  let tokens = working.split(/[\s,]+/).map((t) => t.trim()).filter(Boolean);
  tokens = tokens.filter((t) => !CIVILITES.has(norm(t).replace(/\.$/, "")));
  const nameTokens = tokens.filter((t) => /[a-zA-ZÀ-ÿ]/.test(t));

  if (nameTokens.length) {
    const isUpper = (t) => t.length > 1 && t === t.toUpperCase() && /[A-ZÀ-Ý]/.test(t);
    const uppers = nameTokens.filter(isUpper);
    const others = nameTokens.filter((t) => !isUpper(t));
    if (uppers.length) {
      result.prenom = others.length ? capName(others[0]) : "";
      const extra = others.slice(1);
      result.nom = [...uppers, ...extra].join(" ").toUpperCase();
    } else {
      result.prenom = capName(nameTokens[0]);
      result.nom = nameTokens.slice(1).join(" ").toUpperCase();
    }
  }
  return result;
}

// Champs détectés à partir du texte + ajustements manuels (overrides) + inversion.
// optEmail : email secondaire facultatif (ex. bloc « Email » du volet patient).
export function computeFields(text, overrides = {}, swapped = false, optEmail = "") {
  const base = parseInput(text);
  const bp = swapped ? base.nom : base.prenom;
  const bn = swapped ? base.prenom : base.nom;
  return {
    prenom: overrides.prenom !== undefined ? overrides.prenom : bp,
    nom: overrides.nom !== undefined ? overrides.nom : bn,
    email: (overrides.email !== undefined ? overrides.email : base.email) || optEmail || "",
    phone: overrides.phone !== undefined ? parsePhone(overrides.phone) : base.phone,
  };
}

// État de création à partir des champs : contact présent, prêt à créer, message manquant.
export function fieldStatus(fields) {
  const hasContact = (fields.phone && fields.phone.value && !fields.phone.incomplete) || !!fields.email;
  const canCreate = !!fields.prenom; // moyen de contact (tél/email) facultatif
  const missing = !fields.prenom ? "Ajoutez un prénom" : "";
  return { hasContact, canCreate, missing };
}

// Vrai si la saisie ⌘K ressemble à une personne (pour la ligne « Créer le patient »).
export function looksLikePerson(q) {
  const s = (q || "").trim();
  if (s.length < 2) return false;
  if (/[a-zA-ZÀ-ÿ]{2,}/.test(s)) return true;
  if (EMAIL_RE.test(s)) return true;
  if (PHONE_RE.test(s)) return true;
  return false;
}

// Distance d'édition (Levenshtein) bornée simple.
function lev(a, b) {
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 3) return 99;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return dp[m][n];
}

// Recherche de doublons parmi la patientèle chargée. Renvoie [{ patient, reason }] (max 3).
export function findDuplicates(patients, fields) {
  const out = [];
  const seen = new Set();
  const add = (p, reason, prio) => { if (seen.has(p.id)) return; seen.add(p.id); out.push({ patient: p, reason, prio }); };
  const mob = fields.phone && !fields.phone.incomplete && fields.phone.kind === "mobile" ? fields.phone.value : "";
  const anyPhone = fields.phone && !fields.phone.incomplete ? fields.phone.value : "";
  const email = (fields.email || "").toLowerCase();
  const full = norm(`${fields.prenom} ${fields.nom}`).trim();

  (patients || []).forEach((p) => {
    const pph = normalizePhone(p.telephone || "");
    if (anyPhone && pph && pph === anyPhone) add(p, mob ? "même mobile" : "même numéro", 0);
  });
  (patients || []).forEach((p) => {
    if (email && (p.email || "").toLowerCase() === email) add(p, "même email", 1);
  });
  if (full.length >= 3) (patients || []).forEach((p) => {
    const pn = norm(`${p.prenom} ${p.nom}`).trim();
    if (pn && lev(pn, full) <= 2) add(p, "nom proche", 2);
  });
  return out.sort((a, b) => a.prio - b.prio).slice(0, 3);
}

// "mar. 14 oct." à partir d'un ISO (dernier contact).
export function formatLastContact(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
}

// Initiales (max 2 lettres) pour la pastille d'un doublon.
export function initials(prenom, nom) {
  return `${(prenom || "").trim()[0] || ""}${(nom || "").trim()[0] || ""}`.toUpperCase() || "?";
}
